import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MatLegacyOption as MatOption } from "@angular/material/legacy-core";
import { MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { select, Store } from "@ngrx/store";
import {
	AssessmentAnswer,
	AssessmentAvailableAnswer,
	AssessmentForm,
	AssessmentQuestion,
	CommonState,
	CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT,
	CustomerAssessmentAnswer,
	CustomerAssessmentsAction,
	CustomerAssessmentsSelector,
	UserAnswers,
} from "@obg/ngx.common";
import { AbstractTranslatableSnackBarService, MessageService, MessageType } from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { checkboxRequired } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { find, flatMap, unionBy, xorBy } from "lodash-es";
import { BehaviorSubject, forkJoin, merge, Observable, Subscription } from "rxjs";
import { startWith, take, tap } from "rxjs/operators";

import { CustomerAssessmentsValidatorService } from "./customer-assessments-validator.service";
import { FilterKey, MessageKey } from "./customer-assessments.model";

@Component({
	selector: "obg-m-customer-assessments-container",
	styleUrls: ["./customer-assessments.container.scss"],
	templateUrl: "./customer-assessments.container.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [CustomerAssessmentsValidatorService],
})
export class CustomerAssessmentsContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-customer-assessments-container") hostClass = true;
	@Output() busy = new EventEmitter<boolean>();

	customerAssessmentId: string | undefined;
	assessmentForm: UntypedFormGroup = this.formBuilder.group({});
	assessmentQuestions: AssessmentQuestion[] | undefined;
	assessmentTitle: string | undefined;
	assessmentDescription: string | undefined;
	assessmentSubmitText: string | undefined;

	otherIds: string[] = [];
	answers: UserAnswers = {};
	questionError = new Map<string, { errorMessage: string; testId: string } | undefined>();

	isLoading$ = new BehaviorSubject<boolean>(false);
	data$$: Subscription | undefined;
	fieldValidator$$: Subscription | undefined;

	constructor(
		private formBuilder: UntypedFormBuilder,
		private store: Store<CommonState>,
		private messageService: MessageService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private dialogRef: MatDialogRef<CustomerAssessmentsContainer>,
		private changeDetector: ChangeDetectorRef,
		private customerAssessmentsSelector: CustomerAssessmentsSelector,
		private validatorService: CustomerAssessmentsValidatorService
	) {}

	ngOnInit() {
		this.store.dispatch(CustomerAssessmentsAction.getAssessment(this.customerAssessmentId!));

		const getFormData$ = this.store.pipe(
			select(this.customerAssessmentsSelector.getAssessment()),
			ignoreNil(),
			tap((form: AssessmentForm) => {
				this.assessmentQuestions = this.generateUniqueAnswers(form.assessmentQuestions);
				this.assessmentTitle = form.assessmentTitle;
				this.assessmentDescription = form.assessmentDescription;
				this.assessmentSubmitText = form.assessmentSubmitText;
				this.addFormControls(this.assessmentQuestions);
			})
		);

		const submitAnswersSuccess$ = this.messageService
			.consume$({
				context: [CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT.submitAnswers],
				messageType: MessageType.Success,
			})
			.pipe(
				tap(() => {
					this.openSnackBar(MessageKey.Success, MessageType.Success);
					this.isLoading$.next(false);
					this.closeDialog();
				})
			);

		const submitAnswersError$ = this.messageService
			.consume$({
				context: [CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT.submitAnswers],
				messageType: MessageType.Error,
			})
			.pipe(
				tap(() => {
					this.openSnackBar(MessageKey.Error, MessageType.Error);
					this.isLoading$.next(false);
				})
			);

		const getAssessmentError$ = this.messageService
			.consume$({
				context: [CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT.getAssessment],
				messageType: MessageType.Error,
			})
			.pipe(
				tap(() => {
					this.openSnackBar(MessageKey.Error, MessageType.Error);
					this.isLoading$.next(false);
					this.closeDialog();
				})
			);

		this.data$$ = merge(getFormData$, submitAnswersSuccess$, submitAnswersError$, getAssessmentError$).subscribe(
			() => this.changeDetector.markForCheck()
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.fieldValidator$$?.unsubscribe();
	}

	trackByQuestion(_index: number, question: AssessmentQuestion): string {
		return question.questionId;
	}

	trackByAnswer(_index: number, answer: AssessmentAnswer): string {
		return answer.answerId;
	}

	isOtherOptionSelected(id: string): boolean {
		const cleanedId = this.cleanAnswerId(id);
		return this.otherIds.includes(cleanedId);
	}

	setPlaceholder(question: AssessmentQuestion): string {
		const selectedOther = this.answers[question.questionId]?.find((answer) =>
			this.otherIds.includes(answer.answerId)
		);
		const otherPlaceHolder = question.availableAnswers?.find(
			(available) => available.answerId === selectedOther?.answerId
		);
		return otherPlaceHolder ? `${question.questionText}(${otherPlaceHolder.answerText})` : question.questionText;
	}

	cleanAnswerId(id: string): string {
		return id.replace("assessment-option-", "");
	}

	getOtherId(question: AssessmentQuestion): string {
		return question.availableAnswers.filter((answer) => answer.isOtherOption)[0].answerId;
	}

	getQuestionCategory(questionType: string): string {
		return questionType.split("-")[0];
	}

	getInputType(input: string): string {
		return input.split("-")[1];
	}

	submit() {
		this.isLoading$.next(true);
		this.store.dispatch(
			CustomerAssessmentsAction.submitAnswers(
				{ answers: this.formatAnswersForSubmission() },
				this.customerAssessmentId!
			)
		);
	}

	updateValues(question: AssessmentQuestion, answerText: string, answerId: string, isCheckbox?: boolean) {
		const { questionId, questionText } = question;

		this.updateAnswers({ questionId, questionText, answerText, answerId }, isCheckbox);
	}

	updateMultiSelect(question: AssessmentQuestion, selectChange: MatOption[]) {
		const { questionId, questionText } = question;
		const updatedMultiSelectAnswers = selectChange.map((option) => ({
			questionId,
			questionText,
			answerText: option.value,
			answerId: option.id,
		}));

		this.answers[questionId] = updatedMultiSelectAnswers;
	}

	clearInput(controlName: string) {
		this.assessmentForm.controls[controlName]?.setValue("");
	}

	generateUniqueAnswers(questions: AssessmentQuestion[]): AssessmentQuestion[] {
		return questions.map((question) => {
			const availableAnswers = question.availableAnswers.length
				? this.createUniqueAnswerId(question)
				: this.createAnswer(question);
			return { ...question, availableAnswers };
		});
	}

	setTestId(question: AssessmentQuestion): string {
		const validatorSuffix = question.answerValidators.length
			? `-${question.answerValidators[question.answerValidators.length - 1].validator}`
			: "";
		return `assessment-${question.questionType + validatorSuffix}`;
	}

	isControlTouched(questionId: string): boolean {
		return !!this.assessmentForm.get(questionId)?.touched;
	}

	private buildAvailableAnswersFormGroup(
		answers: AssessmentAvailableAnswer[],
		selectedAnswersIds: string[] = []
	): UntypedFormGroup {
		const formGroup = this.formBuilder.group({}, {});
		answers.forEach((answer) => {
			const isSelected = selectedAnswersIds.some((id) => id === answer.answerId);
			formGroup.addControl(answer.answerId, this.formBuilder.control(isSelected));
		});
		return formGroup;
	}

	private closeDialog() {
		this.dialogRef.close();
	}

	private openSnackBar(messageKey: MessageKey, messageType: MessageType) {
		this.snackBarService
			.openSnackBar$(
				{
					message: messageKey,
					action: MessageKey.Close,
					duration: 4000,
					type: messageType,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private addFormControls(assessmentQuestions: AssessmentQuestion[]) {
		for (const question of assessmentQuestions) {
			this.getOtherOptionIds(question);

			const isCheckboxMandatory = !!(
				question.questionType === "checkbox" &&
				question.answerValidators.find((error) => error.validator === "required")
			);

			if (isCheckboxMandatory) {
				this.assessmentForm.addControl(
					question.questionId,
					new UntypedFormGroup(
						this.buildAvailableAnswersFormGroup(question.availableAnswers).controls,
						checkboxRequired()
					)
				);
			} else {
				this.assessmentForm.addControl(
					question.questionId,
					new UntypedFormControl("", this.validatorService.createValidators(question.answerValidators))
				);
			}

			this.questionError.set(question.questionId, undefined);
		}

		const questionControls: Observable<any>[] = [];

		for (const question of assessmentQuestions) {
			const questionControl = this.assessmentForm.get(question.questionId);
			if (!questionControl) {
				return;
			}

			questionControls.push(
				questionControl.statusChanges.pipe(
					startWith(null),
					tap(() => {
						const validationError = questionControl.errors || undefined;

						if (!validationError) {
							this.questionError.set(question.questionId, undefined);
							return;
						}

						const errorCode = Object.keys(validationError)[0].toLocaleLowerCase();

						const errorMessage =
							find(
								question.answerValidators,
								(validatorObject) => validatorObject.validator.toLocaleLowerCase() === errorCode
							)?.validatorErrorMessage || "";
						const testId = `${question.questionId}-${errorCode}-error`;

						this.questionError.set(question.questionId, {
							errorMessage,
							testId,
						});
					})
				)
			);
		}

		this.fieldValidator$$ = forkJoin(questionControls).subscribe(() => this.changeDetector.markForCheck());
	}

	private getOtherOptionIds(question: AssessmentQuestion) {
		const ids = question.availableAnswers.filter((answer) => answer.isOtherOption).map((answer) => answer.answerId);
		this.otherIds = [...this.otherIds, ...ids];
	}

	private updateAnswers(answer: AssessmentAnswer, isCheckbox?: boolean) {
		const questionId = answer.questionId;
		this.answers[questionId] = isCheckbox
			? xorBy([answer], this.answers[questionId], FilterKey.AnswerId)
			: unionBy([answer], this.answers[questionId], FilterKey.QuestionId);
	}

	private createUniqueAnswerId(question: AssessmentQuestion): AssessmentAvailableAnswer[] {
		return question.availableAnswers.map((answer) => ({
			...answer,
			answerId: `${question.questionId}.${answer.answerId}`,
		}));
	}

	private createAnswer(question: AssessmentQuestion): AssessmentAvailableAnswer[] {
		return [{ answerId: `${question.questionId}.null`, answerText: null, isOtherOption: false }];
	}

	private formatAnswersForSubmission(): CustomerAssessmentAnswer[] {
		return flatMap(this.answers).map((answer) => ({ ...answer, answerId: this.parseAnswerId(answer.answerId) }));
	}

	private parseAnswerId(answerId: string): number | null {
		const id = parseInt(answerId.split(".")[1], 10);
		return isNaN(id) ? null : id;
	}
}
